import React from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import Dial from "./Dial";
import {
  dialValueToNoteValue,
  noteValueToDialValue,
  noteValueToDisplay,
  type NoteValue,
} from "../utils/delayTimeUtils";

export type FXType = "filter" | "reverb" | "delay" | "reverse" | null;

interface FXDrawerProps {
  isOpen: boolean;
  selectedFX: FXType;
  onClose: () => void;

  // Filter controls
  filterCutoff: number;
  onFilterCutoffChange: (value: number) => void;

  // Reverb controls
  reverbDecay: number;
  onReverbDecayChange: (value: number) => void;

  // Delay controls
  delayTime: string; // Tone.js notation (e.g., "4n", "8n")
  delayFeedback: number;
  onDelayTimeChange: (value: string) => void;
  onDelayFeedbackChange: (value: number) => void;
}

const FXDrawer: React.FC<FXDrawerProps> = ({
  isOpen,
  selectedFX,
  onClose,
  filterCutoff,
  onFilterCutoffChange,
  reverbDecay,
  onReverbDecayChange,
  delayTime,
  delayFeedback,
  onDelayTimeChange,
  onDelayFeedbackChange,
}) => {
  // Format frequency for display
  const formatFrequency = (freq: number) => {
    if (freq >= 1000) {
      return `${(freq / 1000).toFixed(1)} kHz`;
    }
    return `${Math.round(freq)} Hz`;
  };

  // Format percentage for display
  const formatPercentage = (value: number) => {
    return `${Math.round(value * 100)}%`;
  };

  // Format note value for display (e.g., "4n" → "1/4")
  const formatNoteValue = (value: number) => {
    // Convert dial value to note value
    const noteValue = dialValueToNoteValue(value);
    return noteValueToDisplay(noteValue);
  };

  // Handle delay time change - convert dial value to note value
  const handleDelayTimeChange = (dialValue: number) => {
    const noteValue = dialValueToNoteValue(dialValue);
    onDelayTimeChange(noteValue);
  };

  const renderFilterControls = () => (
    <DrawerControls id="fx-drawer-filter-controls">
      <h3>Filter Controls</h3>
      <ControlSection id="fx-drawer-filter-control-section">
        <Dial
          value={filterCutoff}
          min={100}
          max={10000}
          onChange={onFilterCutoffChange}
          label="Cutoff"
          color="#FF9800"
          valueFormatter={formatFrequency}
          size={60}
        />
      </ControlSection>
    </DrawerControls>
  );

  const renderReverbControls = () => (
    <DrawerControls id="fx-drawer-reverb-controls">
      <h3>Reverb Controls</h3>
      <ControlSection id="fx-drawer-reverb-control-section">
        <Dial
          value={reverbDecay}
          min={0.5}
          max={10}
          onChange={onReverbDecayChange}
          label="Decay Time"
          color="#2196F3"
          valueFormatter={(val) => `${val.toFixed(1)}s`}
          size={60}
        />
      </ControlSection>
    </DrawerControls>
  );

  const renderDelayControls = () => {
    // Convert note value to dial value for display
    const delayTimeDialValue = noteValueToDialValue(delayTime as NoteValue);

    return (
      <DrawerControls id="fx-drawer-delay-controls">
        <h3>Delay Controls</h3>
        <ControlSection id="fx-drawer-delay-section">
          <Dial
            value={delayTimeDialValue}
            min={0}
            max={1}
            onChange={handleDelayTimeChange}
            label="Time"
            color="#9C27B0"
            valueFormatter={formatNoteValue}
            size={50}
          />
          <Dial
            value={delayFeedback}
            min={0}
            max={0.9}
            onChange={onDelayFeedbackChange}
            label="Feedback"
            color="#9C27B0"
            valueFormatter={formatPercentage}
            size={50}
          />
        </ControlSection>
      </DrawerControls>
    );
  };

  const renderContent = () => {
    switch (selectedFX) {
      case "filter":
        return renderFilterControls();
      case "reverb":
        return renderReverbControls();
      case "delay":
        return renderDelayControls();
      case "reverse":
        return (
          <DrawerPlaceholder id="fx-drawer-reverse-placeholder">
            Reverse has no additional settings
          </DrawerPlaceholder>
        );
      default:
        return (
          <DrawerPlaceholder id="fx-drawer-placeholder">
            Select an effect to edit
          </DrawerPlaceholder>
        );
    }
  };

  const drawerMarkup = (
    <>
      {/* Overlay */}
      {isOpen && <DrawerOverlay id="fx-drawer-overlay" onClick={onClose} />}

      {/* Drawer */}
      <DrawerContainer id="fx-drawer" $isOpen={isOpen}>
        <DrawerHeader id="fx-drawer-header">
          <h2>
            {selectedFX
              ? selectedFX.charAt(0).toUpperCase() + selectedFX.slice(1)
              : "FX Details"}
          </h2>
          <CloseButton id="fx-drawer-close" onClick={onClose}>
            ×
          </CloseButton>
        </DrawerHeader>

        <DrawerContent id="fx-drawer-content">{renderContent()}</DrawerContent>
      </DrawerContainer>
    </>
  );

  return createPortal(drawerMarkup, document.body);
};

export default FXDrawer;

const fadeIn = keyframes`
  to {
    opacity: 1;
  }
`;

const DrawerOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.3);
  z-index: 9998;
  opacity: 0;
  animation: ${fadeIn} 0.3s ease-out forwards;
`;

const DrawerContainer = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: 350px;
  height: 100vh;
  background: linear-gradient(to bottom, #e2e1dd, #d8d4d3);
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
  z-index: 9999;
  transform: ${({ $isOpen }) =>
    $isOpen ? "translateX(0)" : "translateX(100%)"};
  transition: transform 0.3s ease-out;
  overflow-y: auto;
  border-left: 1px solid #ccc;

  @media (max-width: 768px) {
    width: 100vw;
    right: 0;
  }
`;

const DrawerHeader = styled.div`
  position: sticky;
  top: 0;
  background: #cfc9c9;
  padding: 20px;
  border-bottom: 1px solid #b8b8b8;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 10000;

  h2 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #333;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #666;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
    color: #333;
  }
`;

const DrawerContent = styled.div`
  padding: 20px;

  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const DrawerControls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;

  h3 {
    margin: 0 0 20px 0;
    font-size: 16px;
    font-weight: 600;
    color: #333;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 2px solid #8b5cf6;
    padding-bottom: 8px;
  }

  h4 {
    margin: 0 0 15px 0;
    font-size: 14px;
    font-weight: 600;
    color: #555;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }
`;

const ControlSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 15px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.1);
`;

const DrawerPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #999;
  font-size: 16px;
  font-weight: 500;
  text-align: center;
`;
